import type { Me, Settings } from '@wellread/api';

/**
 * The server always sends every group, but a payload restored from an older
 * cache may not. Reading through this means one missing field can never take
 * the whole screen down.
 */
export const DEFAULT_SETTINGS: Settings = {
  profile: { display_name: '', library_label: '', onboarded: false, purpose: 'unset' },
  reader: {
    paper: 'paper',
    set: 'auto',
    size: 19,
    leading: 1.58,
    measure: 66,
    marks: 'study',
    margin: true,
    layout: 'pages',
    page_turn: 'slide',
    lock_bar: false,
    pdf_view: 'text',
    recap_when_away: true,
    recap_after_days: 4,
  },
  session: {
    concepts_per_session: 7,
    grade_mode: 'free',
    target_retention: 0.9,
    timezone: 'UTC',
    favour_bridges: true,
    weakest_first: true,
    show_source: false,
    review_chapters: 'reached',
    marks_at: 'ask',
  },
  chrome: { theme: 'system', density: 'regular', accent: 'ink' },
};

/** The place on Settings, Books & data, where what Explore remembers is: the Explore page links to it. */
export const REMEMBERED = 'what-explore-remembers';

/** A payload that may be missing whole groups, or fields inside them. */
type PartialSettings = { [K in keyof Settings]?: Partial<Settings[K]> };

/** Settings with every group filled in, whatever the payload carried. */
export function settingsOf(me?: Me | { settings?: PartialSettings }): Settings {
  const s = me?.settings;
  return {
    profile: { ...DEFAULT_SETTINGS.profile, ...s?.profile },
    reader: { ...DEFAULT_SETTINGS.reader, ...s?.reader },
    session: { ...DEFAULT_SETTINGS.session, ...s?.session },
    chrome: { ...DEFAULT_SETTINGS.chrome, ...s?.chrome },
  };
}

/**
 * True when the server says this reader hasn't answered the welcome questions.
 * It reads the payload, not the defaults: a cache written before the field
 * existed says nothing either way, and that is never a reason to ask.
 */
export function needsWelcome(me?: { settings?: PartialSettings }): boolean {
  return me?.settings?.profile?.onboarded === false;
}
